import { Fragment } from 'react';

// import case names
import { OVERLAY_VIEWS } from 'constants/templates';

// overlay views
import { errorOverlay as ErrorOverlay } from 'ui/views/widgets/errorOverlay';
import { confirmOverlay as ConfirmOverlay } from 'ui/views/widgets/confirmOverlay';
import { successOverlay as SuccessOverlay } from 'ui/views/widgets/successOverlay';
import { promptOverlay as PromptOverlay } from 'ui/views/widgets/promptOverlay';
import { setDrugPreference as SetDrugPreference } from 'ui/views/widgets/setDrugPreference';
import { AddCreditOverlay } from 'ui/containers/AddCreditOverlay';
import { disableAutoRefill as DisableAutoRefill } from 'ui/views/widgets/disableAutoRefill';
import { changeDefaultConfirm as ChangeDefaultConfirm } from 'ui/views/widgets/changeDefaultConfirm';
import { setPhoneNumber as SetPhoneNumber } from 'ui/views/widgets/setPhoneNumber';
import { addEditCommunicationSettings as AddEditCommunicationSettings } from 'ui/views/widgets/addEditCommunicationSettings';
import { AddEditAddressForm } from 'ui/containers/AddEditAddressForm';
import { MemberDetailsSavingsAlternateDrugsWidget } from 'ui/containers/MemberDetailsSavingsAlternateDrugsWidget';
import { MemberDetailsPayBalanceStepsWidget } from 'ui/containers/MemberDetailsPayBalanceStepsWidget';

import { editHealthInfoAllergies as EditHealthInfoAllergies } from 'ui/views/widgets/editHealthInfoAllergies';
import { editHealthInfoHealthConditions as EditHealthInfoHealthConditions } from 'ui/views/widgets/editHealthInfoHealthConditions';

import { editMemberSecuritySettings as EditMemberSecuritySettings } from 'ui/views/widgets/editMemberSecuritySettings';
import { editMemberSecuritySettingsPassword as EditMemberSecuritySettingsPassword } from 'ui/views/widgets/editMemberSecuritySettingsPassword';
import { editMemberSecuritySettingsSendOtp as EditMemberSecuritySettingsSendOtp } from 'ui/views/widgets/editMemberSecuritySettingsSendOtp';
import { impersonateMember as ImpersonateMember } from 'ui/views/widgets/impersonateMember';
import { memberOrderMarkResolved as MemberOrderMarkResolved } from 'ui/views/widgets/memberOrderMarkResolved';
import { captureReasonCancelOrder as CaptureReasonCancelOrder } from 'ui/views/widgets/captureReasonCancelOrder';
import { CsrMemberNotes } from 'ui/containers/CSRMemberNotes';
import { CsrOrderNotes } from 'ui/containers/CsrOrderNotes';

// import { cSrOrderNotes as CSrOrderNotes } from 'ui/views/widgets/cSROrderNotes';
// import { cSrOrderListNotes as CSrOrderListNotes } from 'ui/views/widgets/cSROrderListNotes';
import { OrderLineItemNotes } from 'ui/containers/OrderLineItemNotes';
import { submitAuthEpostId as SubmitAuthEpostId } from 'ui/views/widgets/submitAuthEpostId';
import { CagFilterOverlay } from 'modules/cagFilter/overlay';
import { notificationOverlay as NotificationOverlay } from 'ui/views/widgets/notificationOverlay';
import { deactiveEpostConfirmation as DeactiveEpostConfirmation } from 'ui/views/widgets/deactiveEpostConfirmation';
import { reactiveEpostConfirmation as ReactiveEpostConfirmation } from 'ui/views/widgets/reactiveEpostConfirmation';
import { CallMarkResolved } from 'ui/containers/CallMarkResolved';
import { wellViewAdministrationEditContact as WellViewAdministrationEditContact } from 'ui/views/widgets/wellViewAdministrationEditContact';
import { editSplashPageContent as EditSplashPageContent } from 'ui/views/widgets/editSplashPageContent';

// import { ClaimReversal } from 'ui/containers/ClaimReversal';
import { ClaimToDo } from 'ui/containers/ClaimToDo';
import { MemberBusinessProcessTodo } from 'ui/containers/MemberBusinessProcessTodo';
import { MemberServicesEscalations } from 'ui/containers/MemberServicesEscalations';
import { viewCase as ViewCase } from 'ui/views/widgets/viewCase';
import { ToDoPrescription } from 'ui/containers/ToDoPrescription';
import { MemberEscalationCall } from 'ui/containers/MemberEscalationCall';
import { OrderToDo } from 'ui/containers/OrderToDo';
import { PriorAuthorization } from 'ui/containers/PriorAuthorization';
import { OutboundCalls } from 'ui/containers/OutboundCalls';
import { WebsiteIssues } from 'ui/containers/WebsiteIssues';
import { MaterialMailOut } from 'ui/containers/MaterialMailOut';
import { StatusOfClaim } from 'ui/containers/StatusOfClaim';
import { AddInsuranceDiscountCardTodo } from 'ui/containers/AddInsuranceDiscountCardTodo';
import { SetPatientNotes } from 'ui/containers/SetPatientNotes';
import { changeNextRefillDate as ChangeNextRefillDate } from 'ui/views/widgets/changeNextRefillDate';
import { CmsWidget } from 'ui/containers/CmsWidget';
import { PersonalIdWidget } from 'ui/containers/PersonalIdWidget';
import { ControlledDrugReorder } from 'ui/containers/ControlledDrugReorder';
import { WellConnectOptIn } from 'ui/containers/WellConnectOptIn';
import { editWellConnectPhone as EditWellConnectPhone } from 'ui/views/widgets/editWellConnectPhone';
import { PaymentCardActivation } from 'ui/containers/PaymentCardActivation';
import { ServiceNow } from 'ui/containers/ServiceNow';
import { ServiceNowQuickForm } from 'ui/containers/ServiceNowQuickForm';
import { confirmationPromptWithIcon as ConfirmationPromptWithIcon } from 'ui/views/generic/confirmationPromptWithIcon';
import { errorNoPermissions as ErrorNoPermissions } from 'ui/views/generic/errorNoPermissions';
import { languagePreferences as LanguagePreferences } from 'ui/views/widgets/languagePreferences';
import { IvrGetPin } from 'ui/views/widgets/IvrGetPin';
import { trackingDetailsOverlayView } from 'ui/views/widgets/trackingDetailsOverlay';

/**
 * @type {function}
 * @desc Resolvers are pure functions used for resolving templates (React components) or to transform some data.
 * A resolver function takes an `id` and an optional `payload` and returns a `result`.
 * Based on `switch` cases inside this function, correct `result` should be returned.
 * @param {object} payload
 * @example
 * import { overlayViewResolver } from 'resolvers/overlayView';
 */
export const overlayViewResolver = ( name ) => {
    switch( name ) {

        // default error view
        case OVERLAY_VIEWS.ERROR_VIEW: {
            return ErrorOverlay;
        }

        // default confirmation view
        case OVERLAY_VIEWS.CONFIRM_VIEW: {
            return ConfirmOverlay;
        }

        // default success view
        case OVERLAY_VIEWS.SUCCESS_VIEW: {
            return SuccessOverlay;
        }

        case OVERLAY_VIEWS.PROMPT_VIEW: {
            return PromptOverlay;
        }

        case OVERLAY_VIEWS.SET_DRUG_PREFERENCE: {
            return SetDrugPreference;
        }

        case OVERLAY_VIEWS.DISABLE_AUTO_REFILL: {
            return DisableAutoRefill;
        }

        case OVERLAY_VIEWS.CHANGE_DEFAULT_CONFIRM: {
            return ChangeDefaultConfirm;
        }

        case OVERLAY_VIEWS.SET_PHONE_NUMBER: {
            return SetPhoneNumber;
        }

        // prior authorization TO DO FORM
        case OVERLAY_VIEWS.PRIOR_AUTHORIZATION: {
            return PriorAuthorization;
        }

        // outbound calls TO DO FORM
        case OVERLAY_VIEWS.OUTBOUND_CALLS: {
            return OutboundCalls;
        }

        // website issues TO DO FORM
        case OVERLAY_VIEWS.WEBSITE_ISSUES: {
            return WebsiteIssues;
        }

        // impersonate member
        case OVERLAY_VIEWS.IMPERSONATE_MEMBER: {
            return ImpersonateMember;
        }

        // add edit contact form for comm. settings
        case OVERLAY_VIEWS.ADD_EDIT_CONTACTS: {
            return AddEditCommunicationSettings;
        }

        // edit contact form for admin contacts
        case OVERLAY_VIEWS.EDIT_ADMIN_CONTACTS: {
            return WellViewAdministrationEditContact;
        }

        // edit splash page content form
        case OVERLAY_VIEWS.EDIT_SPLASH_PAGE_CONTENT: {
            return EditSplashPageContent;
        }
        
        case OVERLAY_VIEWS.ADD_EDIT_ADDRESS: {
            return AddEditAddressForm;
        }

        // add edit allergies form
        case OVERLAY_VIEWS.ADD_EDIT_ALLERGIES: {
            return EditHealthInfoAllergies;
        }

         // add edit health conditions form
         case OVERLAY_VIEWS.ADD_EDIT_HEALTH_CONDITIONS: {
            return EditHealthInfoHealthConditions;
        }

        case OVERLAY_VIEWS.EDIT_SECURITY_SETTING: {
            return EditMemberSecuritySettings;
        }

        case OVERLAY_VIEWS.EDIT_PASSWORD_SECURITY_SETTING: {
            return EditMemberSecuritySettingsPassword;
        }

        case OVERLAY_VIEWS.SECURITY_SETTING_SEND_OTP: {
            return EditMemberSecuritySettingsSendOtp;
        }
        
        case OVERLAY_VIEWS.ALTERNATE_DRUGS: {
            return MemberDetailsSavingsAlternateDrugsWidget;
        }

        case OVERLAY_VIEWS.PAY_BALANCE_STEPS: {
            return MemberDetailsPayBalanceStepsWidget;
        }

        case OVERLAY_VIEWS.MARK_RESOLVED: {
            return MemberOrderMarkResolved;
        }

        case OVERLAY_VIEWS.CAPTURE_REASON_CANCEL_ORDER: {
            return CaptureReasonCancelOrder;
        }
        
        case OVERLAY_VIEWS.CSR_MEMBER_NOTES: {
            return CsrMemberNotes;
        }

        case OVERLAY_VIEWS.CSR_ORDER_NOTES: {
            return CsrOrderNotes;
        }

        case OVERLAY_VIEWS.CSR_ORDER_LINE_NOTES: {
            return OrderLineItemNotes;
        }

        // add/edit epost id
        case OVERLAY_VIEWS.SUBMIT_AUTH_EPOST_ID: {
            return SubmitAuthEpostId;
        }

        // CAG filter
        case OVERLAY_VIEWS.CAG_FILTER: {
            return CagFilterOverlay;
        }

        // CAG filter
        case OVERLAY_VIEWS.NOTIFICATION_VIEW: {
            return NotificationOverlay;
        }

        case OVERLAY_VIEWS.DEACTIVATE_EPOST: {
            return DeactiveEpostConfirmation;
        }

        case OVERLAY_VIEWS.REACTIVATE_EPOST: {
            return ReactiveEpostConfirmation;
        }

        case OVERLAY_VIEWS.CALL_MARK_RESOLVED: {
            return CallMarkResolved;
        }

        case OVERLAY_VIEWS.CLAIM_TODO: {
            return ClaimToDo;
        }

        case OVERLAY_VIEWS.MEMBER_BUSINESS_PROCESS: {
            return MemberBusinessProcessTodo;
        }

        case OVERLAY_VIEWS.MEMBER_SERVICES_ESCALATIONS: {
            return MemberServicesEscalations;
        }

        case OVERLAY_VIEWS.VIEW_CASE: {
            return ViewCase;
        }
        
        case OVERLAY_VIEWS.TO_DO_PRESCRIPTION: {
            return ToDoPrescription;
        }

        case OVERLAY_VIEWS.MEMBER_ESCALATION_CALL: {
            return MemberEscalationCall;
        }

        case OVERLAY_VIEWS.ORDER_TO_DO: {
            return OrderToDo;
        }

        case OVERLAY_VIEWS.MATERIAL_MAIL_OUT: {
            return MaterialMailOut;
        }

        case OVERLAY_VIEWS.STATUS_OF_CLAIM: {
            return StatusOfClaim;
        }

        case OVERLAY_VIEWS.ADD_INSURANCE_DISCOUNT_CARD: {
            return AddInsuranceDiscountCardTodo;
        }

        case OVERLAY_VIEWS.SET_PATIENT_NOTES: {
            return SetPatientNotes;
        }

        case OVERLAY_VIEWS.CHANGE_NEXT_REFILL_DATE: {
            return ChangeNextRefillDate;
        }

        case OVERLAY_VIEWS.CMS_WIDGET: {
            return CmsWidget;
        }
        case OVERLAY_VIEWS.PERSONAL_ID_WIDGET: {
            return PersonalIdWidget;
        }

        case OVERLAY_VIEWS.CONTROLLED_DRUG_REORDER: {
            return ControlledDrugReorder;
        }

        case OVERLAY_VIEWS.WELL_CONNECT_OPT_IN: {
            return WellConnectOptIn;
        }

        case OVERLAY_VIEWS.EDIT_WELL_CONNECT_PHONE: {
            return EditWellConnectPhone;
        }

        case OVERLAY_VIEWS.PAYMENT_CARD_ACTIVATION: {
            return PaymentCardActivation;
        }

        case OVERLAY_VIEWS.SERVICE_NOW: {
            return ServiceNow;
        }

        case OVERLAY_VIEWS.SERVICE_NOW_FORM: {
            return ServiceNowQuickForm;
        }

        case OVERLAY_VIEWS.CONFIRMATION_PROMPT_WITH_ICON: {
            return ConfirmationPromptWithIcon;
        }

        case OVERLAY_VIEWS.ERROR_NO_PERMISSIONS: {
            return ErrorNoPermissions; // no Permissions present Static Overlay
        }

        case OVERLAY_VIEWS.LANGUAGE_PREFERENCE: {
            return LanguagePreferences;
        }

        case OVERLAY_VIEWS.IVR_GET_PIN: {
            return IvrGetPin;
        }
 
        case OVERLAY_VIEWS.TRACKING_NUMBER: {
            return trackingDetailsOverlayView;
        }

        case OVERLAY_VIEWS.ADD_CREDIT_OVERLAY: {
            return AddCreditOverlay
        }

        /*----------------*/

        // default value
        default: {
            return Fragment;
        }
    }
};
